import React from 'react';
import {
  Search, SlidersHorizontal, DollarSign, X,
  ChevronDown, ChevronUp, Star, Globe, Tag, RotateCcw,
} from 'lucide-react';
import { WEBSITE_OPTIONS, RESULTS_SECTION_STRINGS } from '../../constants/appConstants';
import styles from './SearchResultsSidebar.module.scss';

const STRINGS = RESULTS_SECTION_STRINGS.FILTERS;

const SearchResultsSidebar = ({
  filters,
  matchType,
  filtersOpen,
  activeFilterCount,
  uniqueWebsites,
  uniqueBrands,
  priceRange,
  visibleSiteFilters,
  formData,
  onFilterChange,
  onSetFilters,
  onSetMatchType,
  onClearAll,
  onToggleOpen,
}) => (
  <aside className={`${styles.sidebar} ${filtersOpen ? styles.sidebarOpen : ''}`}>
    <div className={styles.sidebarHeader}>
      <div className={styles.sidebarTitle}>
        <SlidersHorizontal size={16} />
        <span>{STRINGS.TITLE}</span>
        {activeFilterCount > 0 && (
          <span className={styles.filterBadge}>{activeFilterCount}</span>
        )}
      </div>
      <div className={styles.sidebarActions}>
        {activeFilterCount > 0 && (
          <button className={styles.clearBtn} onClick={onClearAll}>
            <RotateCcw size={13} />
            {STRINGS.CLEAR_ALL}
          </button>
        )}
        <button className={styles.toggleBtn} onClick={onToggleOpen}>
          {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
    </div>

    {filtersOpen && (
      <div className={styles.filterGroups}>
        {/* Search */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{STRINGS.SEARCH_LABEL}</label>
          <div className={styles.inputWrap}>
            <Search size={14} className={styles.inputIcon} />
            <input
              type="text"
              name="keyword"
              placeholder={STRINGS.SEARCH_PLACEHOLDER}
              value={filters.keyword}
              onChange={onFilterChange}
              className={styles.filterInput}
            />
            {filters.keyword && (
              <button
                className={styles.clearInput}
                onClick={() => onSetFilters((p) => ({ ...p, keyword: '' }))}
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Website Filter */}
        {uniqueWebsites.length > 1 && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <Globe size={13} />
              {STRINGS.WEBSITE_LABEL}
            </label>
            <div className={styles.chipGroup}>
              <button
                className={`${styles.chip} ${!filters.website ? styles.chipActive : ''}`}
                onClick={() => onSetFilters((p) => ({ ...p, website: '' }))}
              >
                {STRINGS.WEBSITE_ALL}
              </button>
              {uniqueWebsites.map((site) => (
                <button
                  key={site}
                  className={`${styles.chip} ${filters.website === site ? styles.chipActive : ''}`}
                  onClick={() =>
                    onSetFilters((p) => ({
                      ...p,
                      website: p.website === site ? '' : site,
                    }))
                  }
                >
                  {WEBSITE_OPTIONS.find((w) => w.value === site)?.label || site}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Brand Filter */}
        {uniqueBrands.length > 1 && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <Tag size={13} />
              {STRINGS.BRAND_LABEL}
            </label>
            <select
              name="brand"
              value={filters.brand}
              onChange={onFilterChange}
              className={styles.filterSelect}
            >
              <option value="">{STRINGS.BRAND_ALL}</option>
              {uniqueBrands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Price Range */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            <DollarSign size={13} />
            {STRINGS.PRICE_RANGE_LABEL}
          </label>
          <div className={styles.priceInputs}>
            <input
              type="number"
              name="minPrice"
              placeholder={STRINGS.MIN_PRICE_PLACEHOLDER(priceRange.min)}
              value={filters.minPrice}
              onChange={onFilterChange}
              className={styles.filterInput}
              min="0"
            />
            <span className={styles.priceSep}>—</span>
            <input
              type="number"
              name="maxPrice"
              placeholder={STRINGS.MAX_PRICE_PLACEHOLDER(priceRange.max)}
              value={filters.maxPrice}
              onChange={onFilterChange}
              className={styles.filterInput}
              min="0"
            />
          </div>
        </div>

        {/* Match Type — not for Shopify */}
        {formData.website !== 'shopify' && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{STRINGS.MATCH_TYPE_LABEL}</label>
            <div className={styles.toggleGroup}>
              <button
                className={`${styles.toggleBtn2} ${matchType === 'fuzzy' ? styles.toggleActive : ''}`}
                onClick={() => onSetMatchType('fuzzy')}
              >
                {STRINGS.MATCH_FUZZY}
              </button>
              <button
                className={`${styles.toggleBtn2} ${matchType === 'exact' ? styles.toggleActive : ''}`}
                onClick={() => onSetMatchType('exact')}
              >
                {STRINGS.MATCH_EXACT}
              </button>
            </div>
          </div>
        )}

        {/* Seller Rating — only for applicable sites */}
        {visibleSiteFilters.sellerRating && (
          <div className={styles.filterGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="hasRating"
                checked={filters.hasRating}
                onChange={onFilterChange}
              />
              <span className={styles.checkmark}></span>
              <Star size={13} />
              {STRINGS.HAS_SELLER_RATING}
            </label>
          </div>
        )}

        {/* Has Image */}
        <div className={styles.filterGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="hasImage"
              checked={filters.hasImage}
              onChange={onFilterChange}
            />
            <span className={styles.checkmark}></span>
            {STRINGS.HAS_PRODUCT_IMAGE}
          </label>
        </div>
      </div>
    )}
  </aside>
);

export default React.memo(SearchResultsSidebar);
